/* eslint-disable object-shorthand */
/* eslint-disable padding-line-between-statements */
import React, { useCallback, useContext, useState, useMemo, useEffect } from 'react';
import _ from 'lodash';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useModal } from '@jutro/components';
import moment from 'moment';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useTranslator } from '@jutro/locale';
import { TWIAResourcesService } from 'twia-capability-constants';
import wizardStyles from 'tfpa-portals-wizard-components-ui/common/TFPAWizard.module.scss'
import ErrorPopupUtil from 'ent-components-platform-react/util/ErrorPopupUtil.js';
import TFPAAdditionalInsuredComponent from '../../components/TFPAAdditionalInsuredComponent/TFPAAdditionalInsuredComponent.jsx';
// eslint-disable-next-line import/no-relative-packages
import _TWIADateUtil from "../../../../modules/twia-portals-viewmodel-js/aspects/_TWIADateUtil";
import metadata from './PolicyDetailsPage.metadata.json5';
import messages from './PolicyDetails.messages.js';

function TFPAPolicyDetailsPage(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const viewModelService = useContext(ViewModelServiceContext)
    const { wizardData: submissionVM, updateWizardData, id } = props;
    const [ pcConstants, setPCConstants ] = useState([]);
    const [dateOfBirthValidationMessage, setDateOfBirthValidationMessage] = useState([]);
    const translator = useTranslator();
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { authHeader } = useAuthentication();
    const { onValidate, isComponentValid, initialValidation } = useValidation(id);
    const [showErrors, setShowErrors] = useState(false);
    const [invalidComponent, setInvalidComponents] = useState([]);
    const { showAlert } = useModal();
    const { refreshData } = useDataRefresh();
    const selectedUWCompany = _.get(submissionVM, 'baseData.associationName_TWIA.value.code');
    const isTWIA = selectedUWCompany === 'twia'
    
    // 2025-11-12 - Debra Frum - DE19606 - PE - TFPA : Unable to create future dated policy past 30 days even after changing the script parameter
    const getPCConstants = useCallback(async () => {
        const constants = await TWIAResourcesService.getPCPortalConstants(authHeader)
        setPCConstants(constants);
    }, [authHeader]);


    // 2025-11-12 - Debra Frum - DE19606 - PE - TFPA : Unable to create future dated policy past 30 days even after changing the script parameter
    useMemo(() => {
        getPCConstants();
    }, [getPCConstants]);

    // 2025-11-12 - Debra Frum - DE19606 - PE - TFPA : Unable to create future dated policy past 30 days even after changing the script parameter
    // Replace hard-coded frontend max effective date validation with use of backend DTO validation which uses script parameters to define the 
    // max effective days in the future for each Association
     useEffect(() => {
         _TWIADateUtil.setMaxEffectiveDays(
             isTWIA
             ? pcConstants.maxEffectiveDateFromTodayForPEPortal_TWIA 
             : pcConstants.maxEffectiveDateFromTodayForPEPortal_TFPA)
     }, [isTWIA, submissionVM,
         pcConstants.maxEffectiveDateFromTodayForPEPortal_TFPA, pcConstants.maxEffectiveDateFromTodayForPEPortal_TWIA]);
 

    const findInvalidComponent = useCallback((isValid, componentID) => {
        setShowErrors(false);

        if (isValid === false) {
            setInvalidComponents((oldValue) => [...oldValue, componentID]);
        }

        if (isValid === true) {
            setInvalidComponents((oldValue) => oldValue.filter((val) => val !== componentID));
        }
    }, []);

    const validateDateOfBirth = useCallback(
        (dob) => {
            const birthDate = moment(dob);
            const today = moment();
            const age = today.diff(birthDate, 'years');

            if (age < 16 || age > 120) {
                return [translator(messages.ageValidationError)];
            }
            return [];
        },
        [translator]
    );

    const onDateOfBirthChange = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);
            const validationMessages = validateDateOfBirth(value);

            if (validationMessages.length > 0) {
                setDateOfBirthValidationMessage(validationMessages);
            } else {
                setDateOfBirthValidationMessage([]);
            }

            updateWizardData(submissionVM);
        },
        [submissionVM, updateWizardData, validateDateOfBirth]
    );

    const onNext = useCallback(async () => {
        if (!isComponentValid || invalidComponent.length > 0) {
            setShowErrors(true);
            return false
        }
        setShowErrors(false);

        try {
            const occupancy = _.get(submissionVM, 'lobData.homeowners_tfpa.coverables.yourHome.occupancy.value');
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.unset(newSubmissionVM.value, 'lobData.homeowners_tfpa.coverables')
            // _.unset(newSubmissionVM.value, 'bindData');

            submissionVM.value = await LoadSaveService.updateDraftSubmission(
                newSubmissionVM.value,
                authHeader
            )
            if (!_.isNil(occupancy?.code) && !_.isEmpty(_.get(submissionVM.value, 'lobData.homeowners_tfpa.coverables.yourHome'))) {
                _.set(submissionVM.value, 'lobData.homeowners_tfpa.coverables.yourHome.occupancy', occupancy.code);
            }
            return submissionVM
        } catch (error) {
            console.log(error)
            ErrorPopupUtil.showSaveErrorPopup(showAlert, false)
            return false
            // return submissionVM
        }
    }, [isComponentValid, invalidComponent.length, viewModelService, submissionVM, 
        LoadSaveService, authHeader, showAlert]);

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, path, value);
            updateWizardData(submissionVM);
            refreshData();
        },
        [refreshData, submissionVM, updateWizardData]
    );

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showRequired: true
        },
        additionalNamedInsuredsDetails: {
            submissionVM,
            updateWizardData,
            findInvalidComponent,
            invalidComponent,
            isComponentValid,
            showErrors
        },
        dateOfBirth: {
            validationMessages: dateOfBirthValidationMessage
        }
    };

    const resolvers = {
        resolveComponentMap: {
            AdditionalInsuredsComponent: TFPAAdditionalInsuredComponent,
        },
        resolveCallbackMap: {
            onDateOfBirthChange: onDateOfBirthChange,
        },
        resolveClassNameMap: wizardStyles
    }

    return (

        <WizardPage
            id={id}
            showPrevious={true}
            onNext={onNext}
            skipWhen={initialValidation}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                showErrors={showErrors}
                overrideProps={overrideProps}
                onValidationChange={onValidate}
                onValueChange={writeValue}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
            />
        </WizardPage>
    );
}

TFPAPolicyDetailsPage.propTypes = wizardProps;
export default TFPAPolicyDetailsPage;
